type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

interface LoggerConfig {
  minLevel: LogLevel;
  enableConsole: boolean;
  enableMetrics: boolean;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private config: LoggerConfig = {
    minLevel: process.env.NODE_ENV === 'production' ? 'info' : 'debug',
    enableConsole: true,
    enableMetrics: process.env.NODE_ENV === 'production',
  };

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextString = context ? ` ${JSON.stringify(context)}` : '';
    return `[${timestamp}] ${level.toUpperCase()}: ${message}${contextString}`;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.minLevel];
  }

  private logToConsole(level: LogLevel, formattedMessage: string) {
    if (!this.config.enableConsole) return;

    switch (level) {
      case 'debug':
        console.debug(formattedMessage);
        break;
      case 'info':
        console.info(formattedMessage);
        break;
      case 'warn':
        console.warn(formattedMessage);
        break;
      case 'error':
        console.error(formattedMessage);
        break;
    }
  }

  private async logToMetrics(level: LogLevel, message: string, context?: LogContext) {
    if (!this.config.enableMetrics) return;

    // Here you would implement logging to your metrics/monitoring service
    // For example, sending to Google Analytics, Sentry, or a custom backend
    try {
      // Example implementation
      const payload = {
        level,
        message,
        context,
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV,
      };

      // You could send this to your metrics service
      // await metricsService.logEvent(payload);
    } catch (error) {
      // Fallback to console if metrics logging fails
      console.error('Failed to log to metrics service:', error);
    }
  }

  private log(level: LogLevel, message: string, context?: LogContext) {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, context);
    this.logToConsole(level, formattedMessage);
    this.logToMetrics(level, message, context);
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, context?: LogContext) {
    this.log('error', message, context);
  }

  setConfig(config: Partial<LoggerConfig>) {
    this.config = { ...this.config, ...config };
  }
}

export const logger = new Logger();
